import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { usePhoenixRefresh } from './usePhoenixRefresh'
import { compressImage } from '../lib/compressImage'
import {
  addLocalPhoto,
  getLocalPhotos,
  deleteLocalPhoto,
} from '../lib/localPhotos'
import { getCached, setCached } from '../lib/hookCache'

const BUCKET = 'progress-photos'
const SIGNED_URL_TTL = 3600 // 1 hour
const CACHE_SCOPE = 'progress_photos'

export function useProgressPhotos() {
  const { user } = useAuth()
  const cached = getCached(CACHE_SCOPE, user?.id)
  const [photos, setPhotos] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(false)
  // Track object URLs we created so we can revoke them on refetch. We
  // deliberately don't revoke on unmount — the cache hands back the
  // assembled photos array on the next mount, including object URLs for
  // local-only photos. Revoking would invalidate the cache. The next
  // successful fetch will revoke the old set before creating new URLs.
  const objectUrlsRef = useRef([])
  const initialLoadedRef = useRef(!!cached)

  const revokeObjectUrls = useCallback(() => {
    for (const url of objectUrlsRef.current) {
      try {
        URL.revokeObjectURL(url)
      } catch {
        /* ignore */
      }
    }
    objectUrlsRef.current = []
  }, [])

  const fetchCloudPhotos = useCallback(async () => {
    if (!supabaseConfigured || !user) return []
    const { data: rows, error: queryErr } = await supabase
      .from('progress_photos')
      .select('*')
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })

    if (queryErr) {
      setError(queryErr.message)
      return []
    }
    if (!rows || rows.length === 0) return []

    const paths = rows.map((r) => r.storage_path)
    const { data: signed, error: urlErr } = await supabase
      .storage
      .from(BUCKET)
      .createSignedUrls(paths, SIGNED_URL_TTL)

    if (urlErr) {
      setError(urlErr.message)
      return []
    }

    return rows.map((row, i) => ({
      ...row,
      signedUrl: signed?.[i]?.signedUrl || null,
      storage: 'cloud',
    }))
  }, [user])

  const fetchAllLocalPhotos = useCallback(async () => {
    if (!user) return []
    const records = await getLocalPhotos(user.id)
    return records.map((r) => {
      const url = URL.createObjectURL(r.blob)
      objectUrlsRef.current.push(url)
      return {
        id: r.id,
        user_id: r.userId,
        date: r.date,
        pose: r.pose,
        caption: r.caption,
        created_at: new Date(r.createdAt).toISOString(),
        signedUrl: url,
        storage: r.pendingCloudUpload ? 'pending' : 'local',
      }
    })
  }, [user])

  const fetchPhotos = useCallback(async () => {
    if (!user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)

    // Revoke previous URLs before re-creating new ones
    revokeObjectUrls()

    const [cloudPhotos, localPhotos] = await Promise.all([
      fetchCloudPhotos(),
      fetchAllLocalPhotos(),
    ])

    const merged = [...cloudPhotos, ...localPhotos].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? 1 : -1
      const ac = a.created_at || ''
      const bc = b.created_at || ''
      return ac < bc ? 1 : ac > bc ? -1 : 0
    })

    setPhotos(merged)
    if (user) setCached(CACHE_SCOPE, user.id, merged)
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, fetchCloudPhotos, fetchAllLocalPhotos, revokeObjectUrls])

  // Trigger a refetch on mount; no unmount cleanup so the cached object
  // URLs remain valid for the next mount.
  useEffect(() => {
    fetchPhotos()
  }, [fetchPhotos])

  usePhoenixRefresh(fetchPhotos)

  // Network errors during fetch surface with no/zero status. We treat
  // those as "user dropped connection mid-upload" and queue the blob.
  const isNetworkError = (err) => {
    if (!err) return false
    const msg = (err.message || '').toLowerCase()
    return (
      msg.includes('failed to fetch')
      || msg.includes('network')
      || msg.includes('load failed')
      || msg.includes('typeerror')
    )
  }

  const queueForLaterUpload = useCallback(async ({ date, pose, caption, blob }) => {
    await addLocalPhoto({
      userId: user.id,
      date,
      pose,
      caption: caption?.trim() || null,
      blob,
      pendingCloudUpload: true,
    })
    await fetchPhotos()
    return true
  }, [user, fetchPhotos])

  const uploadPhoto = async ({ date, pose, caption, file, storage = 'cloud' }) => {
    if (!user) return false
    setError(null)
    setUploading(true)

    try {
      const blob = await compressImage(file)

      if (storage === 'local') {
        await addLocalPhoto({
          userId: user.id,
          date,
          pose,
          caption: caption?.trim() || null,
          blob,
        })
        await fetchPhotos()
        setUploading(false)
        return true
      }

      // Cloud path. If we're already offline, skip the network roundtrip
      // and queue the blob immediately for syncEngine to drain later.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        await queueForLaterUpload({ date, pose, caption, blob })
        setUploading(false)
        return true
      }

      const photoId = crypto.randomUUID()
      const storagePath = `${user.id}/${photoId}.jpg`

      let uploadErr
      try {
        const res = await supabase
          .storage
          .from(BUCKET)
          .upload(storagePath, blob, { contentType: 'image/jpeg', upsert: false })
        uploadErr = res.error
      } catch (e) {
        uploadErr = e
      }

      if (uploadErr) {
        // Treat connection drops mid-upload as "go offline" — queue and
        // let syncEngine retry on reconnect.
        if (isNetworkError(uploadErr)) {
          await queueForLaterUpload({ date, pose, caption, blob })
          setUploading(false)
          return true
        }
        setError(uploadErr.message)
        setUploading(false)
        return false
      }

      let insertErr
      try {
        const res = await supabase
          .from('progress_photos')
          .insert({
            user_id: user.id,
            date,
            pose,
            storage_path: storagePath,
            caption: caption?.trim() || null,
          })
        insertErr = res.error
      } catch (e) {
        insertErr = e
      }

      if (insertErr) {
        // Blob uploaded but row failed. Clean orphan, and if it was a
        // network error queue locally for retry.
        await supabase.storage.from(BUCKET).remove([storagePath]).catch(() => {})
        if (isNetworkError(insertErr)) {
          await queueForLaterUpload({ date, pose, caption, blob })
          setUploading(false)
          return true
        }
        setError(insertErr.message)
        setUploading(false)
        return false
      }

      await fetchPhotos()
      setUploading(false)
      return true
    } catch (e) {
      setError(e.message || 'Upload failed')
      setUploading(false)
      return false
    }
  }

  const deletePhoto = async (photo) => {
    setError(null)

    // Both 'local' (intentional) and 'pending' (offline-queued) photos
    // live in IndexedDB — same delete path.
    if (photo.storage === 'local' || photo.storage === 'pending') {
      await deleteLocalPhoto(photo.id)
      await fetchPhotos()
      return true
    }

    // Cloud delete (existing flow)
    const { error: rowErr } = await supabase
      .from('progress_photos')
      .delete()
      .eq('id', photo.id)

    if (rowErr) {
      setError(rowErr.message)
      return false
    }

    const { error: storageErr } = await supabase
      .storage
      .from(BUCKET)
      .remove([photo.storage_path])

    if (storageErr) {
      console.warn('Storage delete failed:', storageErr.message)
    }

    await fetchPhotos()
    return true
  }

  return {
    photos,
    loading,
    error,
    uploading,
    uploadPhoto,
    deletePhoto,
    refetch: fetchPhotos,
  }
}
